import React, {useEffect, useMemo, useRef} from "react";
import ReportPrintHeader from "../../../Teacher/Reports/ReportPrintHeader";
import useReportSelections from "../../util/useReportSelections";
import BatchPrintButton from '../../blocks/Button/BatchPrintButton'
import { useTranslation } from 'react-i18next';
import ProgressReport from "../../blocks/Charts/Progress";
import classNames from "classnames";
import ParentTips from "./Elements/ParentTips";
import ParentSubScoreList from "./Elements/ParentSubScoreList";
import ParentARMScoreInfo from "./Elements/ParentARMScoreInfo";
import ParentARMBoxes from "./Elements/ParentARMBoxes";
import ParentCanvas from "./Elements/ParentCanvas";
import DisplayLanguageSelect from "../../blocks/Select/DisplayLanguage";
import { createLocalI18n } from "../../../../i18n/i18n";
import { getUserData } from "../../../../services/API";
import { useSelector } from "react-redux";

export default function ParentContent(props) {
  const { name, parentReport, progressReport } = props;

  const progressChartRef = useRef(null);
  const armBoxRefs = useRef({});

  const printLanguage = useSelector(state => state?.reports?.parent?.printLanguage)
  const uiLanguage = printLanguage || getUserData()?.reportingLanguage || 'en';
  const i18n = useMemo(() => createLocalI18n(uiLanguage), []);
  const {t} = useTranslation(['teacher_parent_report', 'common'], {i18n})
  const selections = useReportSelections();

  const isEarlyReader = props.displayStatus === "prereader" ? true : false;
  const { equating, locale, benchmark, customBenchmarks, reportConfig, metric, selectedStatePercentileCuts } = selections;

  let scale = selections.metric.value;

  /**
   * Skip rendering if no data
   */
  if (!parentReport || !progressReport) return null; // do not render if no data
  if (isEarlyReader && scale === 'ORF') return null; // do not render if early reader and ORF

  /**
   * We have report data, so render the report
   */
  const parentReportClass = classNames({
    "parentReport": true,
    "batch-print": props.batchPrinting
  })

  const firstName = name.split(" ")[0];
  let parentReportTitle = t('{{name}}\'s Overall Reading Mastery', {name: name, interpolation: { escapeValue: false },});
  let parentReportTooltip = t(`parent_arm_score_tooltip`);

  if(scale === 'ORF') {
    parentReportTitle = t('Oral Reading Fluency Progress')
    parentReportTooltip = t(`parent_orf_score_tooltip`)
  }

  const getProgressChart = () => {
    if (progressReport?.data?.length > 0) {
      return (
        <ProgressReport
          report={progressReport}
          embedded={true}
          small={false}
          selectedBenchmark={benchmark}
          customBenchmarks={customBenchmarks}
          selectedEquating={equating}
          selectedLocale={locale}
          progressChartRef={progressChartRef}
          selectedStatePercentileCuts={selectedStatePercentileCuts}
          i18n={i18n}
        />
      );
    }
  }

  const i18nLocale = i18n.language;

  return (
    <div className={parentReportClass}>
      <ReportPrintHeader title={t(`Parent Report for {{name}}`, {name: name, interpolation: { escapeValue: false },})} />
      <div className="content">
        <div className="reportSummary">
          <h2 className="sectionHeader">{parentReportTitle}</h2>
          <div className='report-header-controls'>
            <DisplayLanguageSelect label={t('print_language_label')} i18n={i18n}/>
            <BatchPrintButton />
          </div>
        </div>
        <div className="parentProgress">
          <div className="parent-progress-section">
            {metric.value === 'ARM' && <ParentARMScoreInfo studentName={firstName} i18n={i18n} />}
            <ParentARMBoxes armBoxes={parentReport.armBoxes} studentName={firstName} armBoxRefs={armBoxRefs} i18n={i18n} />
          </div>
          <div className='parent-progress-chart'>
            {getProgressChart()}
            <ParentCanvas armBoxRefs={armBoxRefs} progressChartRef={progressChartRef} />
          </div>
        </div>
        {reportConfig.isSubscoresVisible && 
          <ParentSubScoreList
            studentName={firstName}
            subscores={parentReport.subscores}
            i18n={i18n}
          />
        }
        {reportConfig.isTipsVisible && 
          <div className='bottom-content'>
            <div className="parentTips">
              <h3 className="sectionHeader">
                {t(`Tips For Helping {{name}} Right Now`, {name: firstName, interpolation: { escapeValue: false },})}
              </h3>
              <ParentTips tips={parentReport?.tips?.[i18nLocale]} />
            </div>
          </div>
        }
      </div>
    </div>
  );
}
